import React from 'react';
import type { Middleware, SWRHook, SWRResponse } from 'swr';

export type WithLaggy<Response extends SWRResponse = SWRResponse> = Response & {
  isLagging: boolean;
  resetLaggy: () => void;
};

// This is a SWR middleware for keeping the data even if key changes.
const laggy: Middleware = (useSWRNext: SWRHook) => (key, fetcher, config) => {
  // Use a ref to store previous returned data.
  // TODO(jon): This could literally be `any` type since the middleware is
  // passing along whatever data type is coming from the middleware chain and
  // it's not clear in SWR's documentation or a decent chunk of googling if
  // there's a generic or some other way for me to better represent that here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const laggyDataRef = React.useRef<any>();

  // Actual SWR hook.
  const swr = useSWRNext(key, fetcher, config);

  React.useEffect(() => {
    // Update ref if data is not undefined.
    if (swr.data !== undefined) {
      laggyDataRef.current = swr.data;
    }
  }, [swr.data]);

  // Expose a method to clear the laggy data, if any.
  const resetLaggy = React.useCallback(() => {
    laggyDataRef.current = undefined;
  }, []);

  // Fallback to previous data if the current data is undefined.
  const dataOrLaggyData =
    swr.data === undefined ? laggyDataRef.current : swr.data;

  // Is it showing previous data?
  const isLagging =
    swr.data === undefined && laggyDataRef.current !== undefined;

  // Also add a `isLagging` field to SWR.
  return {
    ...swr,
    data: dataOrLaggyData,
    isLagging,
    resetLaggy,
  };
};

export default laggy;
